import React from 'react'
import { WarningFilled } from '@carbon/icons-react'
import { cn } from '../../utils/cn'

interface FormControlContextValue {
  isInvalid?: boolean
  isRequired?: boolean
  isDisabled?: boolean
  id?: string
}

const FormControlContext = React.createContext<FormControlContextValue>({})

export function useFormControlContext() {
  return React.useContext(FormControlContext)
}

export interface FormControlProps extends React.HTMLAttributes<HTMLDivElement> {
  isInvalid?: boolean
  isRequired?: boolean
  isDisabled?: boolean
  id?: string
}

export const FormControl = React.forwardRef<HTMLDivElement, FormControlProps>(
  ({ className, isInvalid, isRequired, isDisabled, id, children, ...props }, ref) => {
    // Generate a stable id so the label and control can be associated
    // (htmlFor/id). This is what makes the field
    // reachable via its label text (accessibility + Playwright getByLabel).
    const generatedId = React.useId()
    const fieldId = id ?? generatedId
    return (
      <FormControlContext.Provider value={{ isInvalid, isRequired, isDisabled, id: fieldId }}>
        <div ref={ref} data-slot="form-control" className={cn('w-full', className)} {...props}>
          {children}
        </div>
      </FormControlContext.Provider>
    )
  },
)
FormControl.displayName = 'FormControl'

export const Label = React.forwardRef<
  HTMLLabelElement,
  React.LabelHTMLAttributes<HTMLLabelElement>
>(({ className, ...props }, ref) => (
  <label
    ref={ref}
    className={cn('text-sm font-medium text-content-secondary', className)}
    {...props}
  />
))
Label.displayName = 'Label'

export const FormLabel = React.forwardRef<
  HTMLLabelElement,
  React.LabelHTMLAttributes<HTMLLabelElement> & { requiredIndicator?: React.ReactNode }
>(({ className, children, requiredIndicator, htmlFor, ...props }, ref) => {
  const { isRequired, id } = useFormControlContext()
  return (
    <label
      ref={ref}
      htmlFor={htmlFor ?? id}
      className={cn('mb-1.5 block text-sm font-medium text-content-secondary', className)}
      {...props}
    >
      {children}
      {isRequired &&
        (requiredIndicator ?? (
          <>
            <span aria-hidden="true" className="ml-0.5 text-danger">
              *
            </span>
            <span className="sr-only"> (required)</span>
          </>
        ))}
    </label>
  )
})
FormLabel.displayName = 'FormLabel'

export const FormHelperText = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p ref={ref} className={cn('mt-1 text-xs text-content-muted', className)} {...props} />
))
FormHelperText.displayName = 'FormHelperText'

export const FormErrorMessage = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, children, ...props }, ref) => {
  const { isInvalid } = useFormControlContext()
  if (isInvalid === false || !children) return null
  return (
    <p
      ref={ref}
      role="alert"
      className={cn('mt-1 flex items-start gap-1 text-xs text-danger', className)}
      {...props}
    >
      <WarningFilled size={14} className="mt-px shrink-0" aria-hidden="true" />
      <span>{children}</span>
    </p>
  )
})
FormErrorMessage.displayName = 'FormErrorMessage'
